<script module lang="ts">
  import type { UserViewFrameContext } from "./_shared/_logic/user-types"
  export type { UserViewFrameContext } from "./_shared/_logic/user-types"
</script>

<script lang="ts">
  import { onMount, untrack } from "svelte"
  import type { OrganizationFrame, OrganizationUser } from "@kontextwerk/control-web-client"
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { registerNavigationGuard } from "@kontextwerk/web-sdk/core/routing/navigation"
  import type { RecordDetailConfig } from "@kontextwerk/web-sdk/ui/layout/recordDetail/types"
  import { buildUserHeader, userRecordIcon } from "./_logic/header"
  import { resolveUserMember } from "./_logic/member"
  import {
    bindMemberAccess,
    isWithinMemberRecord,
    memberAccessKey,
  } from "./_logic/memberAccessBinding"
  import UserViewFrameWrapper from "@kontextwerk/control-web-frame/shared/modules/user-frame-loader"
  import ActivityContextPanel from "@kontextwerk/control-web-frame/shared/modules/activity-context-panel"
  import { composeControlActivityCatalog } from "@kontextwerk/control-web-frame/shared/logic/activity"
  import { frameConfig as activityFrameConfig } from "@kontextwerk/control-web-frame/shared/logic/frame-state"
  import GeneralPage from "./pages/general"
  import PermissionsPage from "./pages/permissions"
  import IntegrationsPage from "./pages/integrations"
  import WorkingHoursPage from "./pages/working-hours"
  import type { MemberAccess } from "./_shared/_logic/member-access"
  import { location } from "../../../../_shared/_logic/store"
  import { getAdminApi } from "../../../../api/kehrblatt/client"
  import { loadKehrblattOrganizationFrame } from "../../../../api/kehrblatt/organization-frame"
  import { NO_PERMISSIONS } from "../../../_shared/_logic/permissions-model"
  import { buildUserViewNavigation, buildUserViewPaths } from "./navigation"
  import {
    getKehrblattControlAuthContext,
    isSupportDelegationActive,
  } from "../../../_shared/_logic/auth-context"
  import { userDisplayName } from "@kontextwerk/control-web-frame/shared/logic/user-presentation"

  const {
    userId = null,
    organizationId = null,
    organizationSlug = null,
    organizationResolvedId = null,
  } = $props<{
    userId?: string | null
    organizationId?: string | null
    organizationSlug?: string | null
    organizationResolvedId?: string | null
  }>()

  let frameConfig = $state<any>(null)
  const activityCatalog = $derived(
    composeControlActivityCatalog($activityFrameConfig?.activity?.catalog),
  )
  const authContext = getKehrblattControlAuthContext()
  // Member lookups stay inside the confirmed organization; the KB roster carries the membership
  // status next to the admitted labels. There is no global directory path.
  const userViewLoaders = {
    fetchOrganization: (id: string): Promise<OrganizationFrame> =>
      loadKehrblattOrganizationFrame(id, {}),
  }

  const user = $derived(frameConfig?.user ?? null)
  const organizationIdForUser = $derived(
    frameConfig?.orgContext?.id ?? frameConfig?.resolvedOrganizationId ?? null,
  )
  const administrationPath = $derived.by(() => {
    if (!organizationId && !organizationSlug) return null

    const segment = frameConfig?.orgContext?.slug ?? organizationSlug ?? organizationId

    return segment ? `/organizations/${encodeURIComponent(segment)}/administration` : null
  })
  const usersListPath = $derived(administrationPath ? `${administrationPath}/users` : null)
  const tabPaths = $derived.by(() => buildUserViewPaths(frameConfig?.tabPath))

  const headerTitle = $derived(userDisplayName(user) || "Benutzer")
  const headerSubtitle = $derived(user?.email ?? frameConfig?.orgContext?.name ?? "Benutzerdetails")
  const loading = $derived(frameConfig?.loading ?? false)
  const error = $derived(frameConfig?.error ?? null)
  const permissions = $derived($authContext?.permissions ?? NO_PERMISSIONS)
  const session = $derived($authContext?.session ?? null)
  const resolvedTargetUserId = $derived(
    user?.id && (!userId || user.id === userId) ? user.id : null,
  )
  // Route identity; without a user id the profile belongs to the signed-in user.
  const routeRecordKey = $derived(
    `user:${organizationSlug ?? organizationId ?? "global"}:${userId ?? "self"}`,
  )
  const confirmedUser = $derived(resolvedTargetUserId && !error ? user : null)
  const member = $derived(
    confirmedUser
      ? resolveUserMember(frameConfig?.organization ?? null, confirmedUser.id, session)
      : null,
  )
  const supportDelegationActive = $derived(isSupportDelegationActive(session))
  // Personal pages and integrations belong to the actual signed-in user's own record only, never
  // to another member and never to a represented member during support delegation.
  const isOwnAccount = $derived(
    Boolean(
      resolvedTargetUserId &&
      !supportDelegationActive &&
      resolvedTargetUserId === ($authContext?.user?.id ?? null),
    ),
  )
  const canViewCalendarIntegration = $derived(isOwnAccount)
  const canManageCalendarIntegration = $derived(isOwnAccount)
  const canDisconnectOwnCalendarIntegration = $derived(isOwnAccount)

  // One access controller per confirmed context and member; a change of either discards it.
  const accessKey = $derived(
    memberAccessKey(session, member, organizationIdForUser ? String(organizationIdForUser) : null),
  )
  let memberAccess = $state<MemberAccess | null>(null)
  const currentMemberAccess = $derived(
    memberAccess && member && memberAccess.membershipId === member.membershipId.toLowerCase()
      ? memberAccess
      : null,
  )

  // effect-ok: async-load -- binds one access controller to the confirmed member identity and
  // disposes it, including its pending reads, when that identity or context changes.
  $effect(() => {
    if (!accessKey) return

    const controller = untrack(() => bindMemberAccess(session, permissions, member))

    if (!controller) return

    memberAccess = controller
    void controller.load()

    return () => {
      controller.dispose()

      if (memberAccess === controller) memberAccess = null
    }
  })

  const instanceId = $props.id()

  // Pages and tabs of the same member keep the role draft; leaving the record asks first.
  onMount(() => {
    const unregister = registerNavigationGuard(
      `user-member-access-${instanceId}`,
      (to, _options, resume) => {
        const access = currentMemberAccess

        if (
          !access ||
          access.status !== "ready" ||
          isWithinMemberRecord(to, frameConfig?.userBasePath)
        )
          return true

        if (access.saving) return false

        if (!access.dirty) return true

        void newApproval({
          title: "Ungespeicherte Rollenänderung",
          description: "Änderungen an Rolle und Status verwerfen?",
          approvalLabel: "Verwerfen",
          declineLabel: "Weiter bearbeiten",
          approvalCallback: () => {
            if (access.saving) return

            access.discard()
            resume?.()
          },
        })

        return false
      },
    )
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (currentMemberAccess?.dirty || currentMemberAccess?.saving) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", beforeUnload)

    return () => {
      unregister()
      window.removeEventListener("beforeunload", beforeUnload)
    }
  })

  const recordDetail = $derived<RecordDetailConfig>({
    key: routeRecordKey,
    loading: !confirmedUser && (frameConfig?.loading ?? true),
    header: confirmedUser
      ? buildUserHeader(
          {
            role: currentMemberAccess?.currentRoleName ?? null,
            email: currentMemberAccess?.record?.email ?? (confirmedUser.email?.trim() || null),
          },
          frameConfig?.orgContext ?? null,
        )
      : { icon: userRecordIcon, fields: [] },
  })
  // Tab content deliberately remounts once the target user resolves; the record frame does not.
  const viewKey = $derived(
    `user:${organizationSlug ?? organizationId ?? "global"}:` +
      (resolvedTargetUserId ? `resolved:${resolvedTargetUserId}` : `loading:${userId ?? "none"}`),
  )

  const retryLoad = () => {
    frameConfig?.reload()
  }

  const handleUserUpdated = (updated: OrganizationUser) => {
    frameConfig?.setUser(updated)
  }

  const viewFrameContext = $derived.by<UserViewFrameContext>(() => ({
    user,
    member,
    currentUserId: $authContext?.currentUserId ?? null,
    targetUserId: userId ?? $authContext?.currentUserId ?? null,
    organizationId: organizationIdForUser,
    loading,
    error,
    onRetry: retryLoad,
    onUpdated: handleUserUpdated,
    permissions,
    isOwnAccount,
    memberAccess: currentMemberAccess,
    canViewCalendarIntegration,
    canManageCalendarIntegration,
    canDisconnectOwnCalendarIntegration,
  }))

  const pageNavigation = $derived.by(() =>
    buildUserViewNavigation({
      context: viewFrameContext,
      paths: tabPaths,
      snippets: {
        general: generalPageContent,
        permissions: permissionsPageContent,
        integrations: integrationsPageContent,
        workingHours: workingHoursPageContent,
      },
    }),
  )
</script>

{#snippet generalPageContent(activeTab: string)}
  <GeneralPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet permissionsPageContent(activeTab: string)}
  <PermissionsPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet integrationsPageContent(activeTab: string)}
  <IntegrationsPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet workingHoursPageContent(activeTab: string)}
  <WorkingHoursPage {viewFrameContext} {activeTab} />
{/snippet}

<UserViewFrameWrapper
  {userId}
  {organizationId}
  {organizationSlug}
  {organizationResolvedId}
  defaultTab="general"
  loaders={userViewLoaders}
  bind:config={frameConfig}
>
  {#snippet viewFrame(_config)}
    {#if user && organizationIdForUser && resolvedTargetUserId && !loading && !error}
      <ActivityContextPanel
        context={{
          organization_id: organizationIdForUser,
          object_type: "user",
          object_id: resolvedTargetUserId,
        }}
        label={headerTitle}
        operations={getAdminApi().activity}
        catalog={activityCatalog}
      />
    {/if}
    <PageTemplate
      {viewKey}
      {recordDetail}
      title={headerTitle}
      subtitle={headerSubtitle}
      {pageNavigation}
      appBreadcrumbs={administrationPath && usersListPath
        ? [
            { title: "Verwaltungsbereich", href: administrationPath },
            { title: "Benutzer", href: usersListPath },
            { title: headerTitle },
          ]
        : undefined}
      locationStore={location}
      gobackLink={usersListPath ?? ""}
    />
  {/snippet}
</UserViewFrameWrapper>
